import type { KeySequence } from './types.tsx';

/**
 * Converts a KeyboardEvent to a normalised string representation of
 * modifiers and key.
 *
 * Ordering of modifier keys is taken from Apple's guidelines:
 * https://developer.apple.com/macos/human-interface-guidelines/input-and-output/keyboard/
 *
 * Example representation of ArrowUp key with all modifiers present:
 * ctrl+shift+meta+arrowup
 *
 * Character keys never include the 'shift' modifier,.
 * as it changes the character itself.
 * Examples:
 *  - instead of 'shift+a' the normal form is 'A'
 *  - instead of 'shift+/' the normal form is '?'
 *
 * The 'alt' modifier is NOT supported at all due to inconsistent behaviour
 * across platforms. On Mac, it changes the character input, e.g.
 * 'alt+z' is 'Ω' - which becomes the `event.key`. To properly support
 * this, a reverse-mapping of all these characters needs to be added.
 * Alternatively, `String.fromCharCode(event.which)` can be used, but
 * 1. This is a deprecated property
 * 2. The char code is still for the modified character for some keys on Mac
 *    (e.g. The char code for 'alt+/' is '¿')
 */

// Special keys such as 'Escape', 'ArrowUp' or ' '
const isNonCharacterKey = (e: KeyboardEvent) => e.key.length > 1 || e.key === ' ';
const modifiers = [
	{
		name: 'ctrl',
		// @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
		test: (e) => e.ctrlKey,
	},
	{
		name: 'shift',
		// @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
		test: (e) => e.shiftKey && isNonCharacterKey(e),
	},
	{
		// Command key on MacOS, Windows key on Windows
		name: 'meta',
		// @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
		test: (e) => e.metaKey || (e.getModifierState && e.getModifierState('OS')),
	},
];
const toKey = (e: KeyboardEvent) => {
	if (isNonCharacterKey(e)) {
		return e.key.toLowerCase();
	}
	// Ignore accidental CapsLock
	return e.shiftKey ? e.key.toUpperCase() : e.key.toLowerCase();
};
export const toKeyWithModifiers = (e: KeyboardEvent): KeySequence => {
	if (!e || !e.key) {
		// There are still browsers out there that do not support e.key;
		// Just today (Oct 2018) I have observed Chrome 47 in Sentry, but it started supporting e.key since 51.
		// So instead of crashing the page with TypeError let's not detect keyboard shortcuts.
		return {
			sequence: '',
			e,
		};
	}
	return {
		sequence: modifiers
			.filter((modifier) => modifier.test(e))
			.map((modifier) => modifier.name)
			.concat(toKey(e))
			.join('+'),
		e,
	};
};

/*
 *  Selected special key values disjunction for regex. All lowercase, ordered alphabethically.
 *  See https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key/Key_Values.
 */
const orSpecialChars =
	' |arrowdown|arrowleft|arrowright|arrowup|backspace|clear|delete|end|enter|escape|home|insert|pagedown|pageup|pause|printscreen|tab';

/*
 * This string when used as a regular expression:
 * 1. Allows lowercase only with a single exception (see 3)
 * 2. Allows optional ctrl, shift, meta followed by "+" in that specific order in the beginning
 * 3. Allows any printable character (may be uppercase letter) or a special key value
 * 4. If shift modifier is used it can only be followed by a special char
 * 5. Is based on toKeyWithModifiers, uses orSpecialChars whitelist for shift chars
 * 6. Does not capture redundant groups
 */
const singleShortcutEntry = `(ctrl\\+)?(shift\\+(?=(?:(?:meta\\+)?(?:${orSpecialChars}))))?(meta\\+)?(${orSpecialChars}|[!-~])`;

/*
 * Any number of singleShortcutEntry separated by a single space
 * We are not specifying "g" flag, so it's fine to reuse the same regex object.
 */
const shortcutRegex = RegExp(`^${singleShortcutEntry}( ${singleShortcutEntry})*$`);
export const isValidShortcut = (shortcut: string) => shortcutRegex.test(shortcut);
/**
 * Checks for a specific type of overlap between two shortcuts, where one may be a prefix of the other, separated by a space, to ensure they are distinct commands.
 */
export const isShortcutCollision = (leftShortcut: string, rightShortcut: string) => {
	if (!leftShortcut || !rightShortcut) {
		return leftShortcut === rightShortcut;
	}
	if (rightShortcut === leftShortcut) {
		return true;
	}
	if (leftShortcut.indexOf(rightShortcut) === 0) {
		return leftShortcut[rightShortcut.length] === ' ';
	}
	if (rightShortcut.indexOf(leftShortcut) === 0) {
		return rightShortcut[leftShortcut.length] === ' ';
	}
	return false;
};
